import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    FaSearch, FaHome, FaBookOpen, FaDownload,
    FaStar, FaCog, FaChevronLeft, FaChevronRight, FaPlayCircle,
    FaFileAlt, FaEye, FaRobot, FaPaperPlane, FaTimes,
    FaChevronDown, FaChevronUp, FaExclamationTriangle, FaCheck,
    FaCalendarAlt, FaArrowLeft
} from 'react-icons/fa';
import SafeLink from '../components/SafeLink';
import PremiumUpgrade from '../components/PremiumUpgrade';
import VideoModal from '../components/VideoModal';
import FilePreviewModal from '../components/FilePreviewModal';
import { resourcesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './ResearcherLibrary.css';

// ── Helper function to map resources from BD to card format ────────────────
const mapResourceToCard = (resource) => {
    const colors = ['#FF6600', '#22c55e', '#a855f7', '#f59e0b', '#0891b2'];
    const thumbTypes = ['template', 'guide', 'video', 'article', 'poster'];
    const gradients = [
        'linear-gradient(135deg, #2d2010 0%, #3d2a18 50%, #4a3a1a 100%)',
        'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        'linear-gradient(135deg, #2d132c 0%, #ee4540 60%, #c72c41 100%)',
        'linear-gradient(135deg, #3a1c71 0%, #d76d77 100%)',
        'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
    ];

    const idx = resource.id % colors.length;
    const ext = resource.fileName ? resource.fileName.split('.').pop() : 'doc';

    return {
        id: resource.id,
        type: resource.type?.name || 'Recurso',
        title: resource.name,
        desc: resource.description || 'Sin descripción',
        fileType: ext.toUpperCase(),
        fileSize: Math.floor(Math.random() * 20) + 1 + 'MB',
        action: 'Descargar',
        actionIcon: 'download',
        thumb: thumbTypes[idx],
        thumbBg: gradients[idx],
        color: colors[idx],
        date: new Date(resource.createdAt).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).replace(/\./g, ''),
        views: Math.floor(Math.random() * 10000) + 1000,
        fileName: resource.fileName,
        // Almacenar el recurso original completo para VideoModal
        originalResource: resource,
    };
};

// ── MOCK DATA (replaced with real data from BD) ────────────────────────────
const RESOURCES = [];

// ── Mock AI responses ──────────────────────────────────────────────────────
const AI_RESPONSES = {
    resumir: [
        'Este recurso presenta una plantilla estandarizada para manuscritos destinados a revistas Q1. Incluye estructura IMRAD, normas de formato, referencias en estilo Vancouver, y recomendaciones para mejorar la probabilidad de aceptación en revistas de alto impacto.',
        'Guía actualizada en 2024 sobre principios éticos fundamentales en investigación clínica internacional. Cubre consentimiento informado, confidencialidad de datos, conflictos de interés, y cumplimiento de regulaciones GCP actuales.',
        'Tutorial avanzado en programación estadística con R. Domina librerías de visualización (ggplot2) y manipulación de datos (dplyr) para análisis epidemiológicos robustos con ejemplos prácticos implementables.',
    ],
    explicar: [
        'Las revistas Q1 son las de mayor impacto en su disciplina. Esta plantilla te ayuda a formatear tu trabajo correctamente, reduciendo rechazos por problemas formales. Aumenta tus posibilidades de que los revisores se enfoquen en la calidad científica.',
        'Los protocolos éticos garantizan que tu investigación respeta derechos humanos y estándares internacionales. Si los incumples, tu estudio será rechazado por comités de ética y no podrá publicarse en revistas serias.',
        'R es el lenguaje estándar en epidemiología y bioestadística. Estos comandos te permiten crear visualizaciones publicables y análisis reproducibles que otros investigadores podrán verificar independientemente.',
    ],
};

const AI_ACTIONS = [
    { key: 'resumir', label: 'Resumir', icon: <FaFileAlt />, hint: 'Resumen ejecutivo' },
    { key: 'explicar', label: 'Explicar', icon: <FaBookOpen />, hint: 'Explicación detallada' },
];

const TABS = [
    { key: 'todos', label: 'Todos', icon: <FaHome /> },
    { key: 'plantilla', label: 'Formatos/Plantillas', icon: <FaFileAlt /> },
    { key: 'guía', label: 'Guías Metodológicas', icon: <FaBookOpen /> },
    { key: 'video', label: 'Vídeos Tutoriales', icon: <FaPlayCircle /> },
    { key: 'destacado', label: 'Artículos Destacados', icon: <FaStar /> },
];

const ITEMS_PER_PAGE = 6;
const STORAGE_USED = 8.5;
const STORAGE_TOTAL = 10;



// ── Resource Card ──────────────────────────────────────────────────────────
const ResourceCard = ({ resource, onVideoClick, onFileClick, isSelected = false, onToggleSelect }) => {
    // Detectar video por tipo o por extensión de archivo
    const isVideo = resource.type === 'Video' ||
        (resource.fileName && /\.(mp4|webm|ogv|avi|mov|mkv)$/i.test(resource.fileName)) ||
        (resource.originalResource?.type?.name?.toLowerCase() === 'video');

    const handleCardClick = () => {
        if (isVideo && onVideoClick) {
            const videoResource = {
                ...resource,
                ...(resource.originalResource && {
                    name: resource.originalResource.name,
                    type: resource.originalResource.type,
                    category: resource.originalResource.category,
                    filePath: resource.originalResource.filePath
                })
            };
            onVideoClick(videoResource);
        } else if (!isVideo && onFileClick) {
            onFileClick(resource);
        }
    };

    const handleSelectClick = (e) => {
        e.stopPropagation();
        if (onToggleSelect) onToggleSelect(resource.id);
    };

    return (
        <div
            className={`rl-card${isSelected ? ' rl-card--selected' : ''}`}
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
        >
            {/* Thumbnail — con iconos persistentes 📁 */}
            <div className="rl-card-thumb" style={{ background: resource.thumbBg }}>
                {/* Círculo de selección estilo Google Photos */}
                <button
                    className={`rl-select-circle${isSelected ? ' rl-select-circle--active' : ''}`}
                    onClick={handleSelectClick}
                    title={isSelected ? 'Deseleccionar' : 'Seleccionar para IA'}
                    aria-label={isSelected ? 'Deseleccionar recurso' : 'Seleccionar recurso'}
                >
                    {isSelected && <FaCheck className="rl-select-check" />}
                </button>

                {/* Overlay de selección */}
                {isSelected && <div className="rl-select-overlay" />}

                {isVideo
                    ? <div className="rl-thumb-play-btn"><FaPlayCircle /></div>
                    : <div className="rl-thumb-icon">
                        {resource.type?.toLowerCase().includes('guía') ? <FaBookOpen /> : <FaFileAlt />}
                    </div>
                }

                <span
                    className="rl-thumb-badge"
                    style={{ color: resource.color, borderColor: resource.color }}
                >
                    {resource.type?.toUpperCase()}
                </span>
                {isVideo && resource.fileSize && (
                    <span className="rl-thumb-duration">{resource.fileSize}</span>
                )}
            </div>

            {/* Card body */}
            <div className="rl-card-body">
                <h3 className="rl-card-title">{resource.title}</h3>
                <p className="rl-card-desc">{resource.desc}</p>
                <div className="rl-card-meta">
                    <span style={{ color: resource.color }}><FaCalendarAlt /> {resource.date}</span>
                    <span style={{ color: resource.color }}><FaEye /> {resource.views.toLocaleString()}</span>
                </div>
                <button className="rl-card-btn" onClick={handleCardClick}>
                    {isVideo ? <><FaPlayCircle /> Reproducir</> : <><FaEye /> Ver Recurso</>}
                </button>
            </div>
        </div>
    );
};

// ── AI Assistant Panel for Researchers ─────────────────────────────────────
const ResearcherAiPanel = ({ selectedResources = new Set(), onClearSelection, resources = [] }) => {
    const [question, setQuestion] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [lastActionKey, setLastActionKey] = useState(null);
    const chatEndRef = useRef(null);

    // Obtener títulos de recursos seleccionados
    const selectedResourcesList = resources.filter(r => selectedResources.has(r.id));

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const simulateResponse = (type) => {
        console.log('📋 simulateResponse called with type:', type);
        setLoading(true);
        setLastActionKey(type);
        console.log('📋 lastActionKey set to:', type);
        const pool = AI_RESPONSES[type];
        const text = pool[Math.floor(Math.random() * pool.length)];
        const userMsg = AI_ACTIONS.find(a => a.key === type)?.label || type;

        setMessages(prev => [...prev, { role: 'user', text: userMsg, action: type }]);

        setTimeout(() => {
            setMessages(prev => [...prev, { role: 'ai', text, action: type }]);
            setLoading(false);
        }, 1200);
    };

    const handleSend = () => {
        if (!question.trim()) return;
        const q = question.trim();
        setQuestion('');
        setLastActionKey(null);
        setMessages(prev => [...prev, { role: 'user', text: q }]);
        setLoading(true);
        setTimeout(() => {
            setMessages(prev => [...prev, {
                role: 'ai',
                text: 'Basándome en nuestro repositorio de recursos: ' + q.slice(0, 40) + '... es un tema cubierto en nuestras plantillas, guías y tutoriales. Recomiendo revisar los recursos asociados a esta temática en las diferentes secciones.'
            }]);
            setLoading(false);
        }, 1400);
    };

    return (
        <aside className={`rl-ai-panel ${collapsed ? 'rl-ai-panel--collapsed' : ''}`}>
            {/* Panel header */}
            <div className="rl-ai-panel-header">
                <div className="rl-ai-panel-title">
                    <div className="rl-ai-status-dot" />
                    <FaRobot className="rl-ai-robot-icon" />
                    <div>
                        <span className="rl-ai-panel-name">Asistente IA Research</span>
                        <span className="rl-ai-online-label">En línea</span>
                    </div>
                </div>
                <button className="rl-ai-collapse-btn" onClick={() => setCollapsed(c => !c)}>
                    {collapsed ? <FaChevronDown /> : <FaChevronUp />}
                </button>
            </div>

            {!collapsed && (
                <>
                    {/* Disclaimer */}
                    <div className="rl-ai-disclaimer">
                        <FaExclamationTriangle className="rl-ai-disclaimer-icon" />
                        <p>Este asistente <strong>resume y explica</strong> recursos. <em>Ideal para entender rápidamente contenido técnico.</em></p>
                    </div>

                    {/* Quick action buttons */}
                    <div className="rl-ai-quick-actions">
                        {/* Recursos seleccionados */}
                        {selectedResources.size > 0 && (
                            <div className="rl-ai-selected-resources">
                                <p className="rl-ai-selected-label">
                                    {selectedResources.size} recurso{selectedResources.size > 1 ? 's' : ''} seleccionado{selectedResources.size > 1 ? 's' : ''}
                                </p>
                                <div className="rl-ai-selected-list">
                                    {selectedResourcesList.map(r => (
                                        <div key={r.id} className="rl-ai-selected-item">
                                            {r.title}
                                        </div>
                                    ))}
                                </div>
                                <button
                                    className="rl-ai-clear-btn"
                                    onClick={onClearSelection}
                                    disabled={loading}
                                >
                                    Limpiar selección
                                </button>
                            </div>
                        )}

                        <p className="rl-ai-quick-label">Acciones rápidas</p>
                        <div className="rl-ai-actions-grid">
                            {AI_ACTIONS.map(action => (
                                <button
                                    key={action.key}
                                    className={`rl-ai-action-btn ${lastActionKey === action.key ? 'active' : ''}`}
                                    onClick={() => simulateResponse(action.key)}
                                    disabled={loading}
                                >
                                    <span className="rl-ai-action-icon">{action.icon}</span>
                                    <span>{action.label}</span>
                                    {lastActionKey === action.key && <FaCheck className="rl-ai-check" />}
                                </button>
                            ))}
                        </div>

                        {/* Selected action indicator */}
                        {lastActionKey && (
                            <div className="rl-selected-action">
                                <FaCheck className="rl-selected-check" />
                                <span className="rl-selected-text">
                                    {AI_ACTIONS.find(a => a.key === lastActionKey)?.icon} {AI_ACTIONS.find(a => a.key === lastActionKey)?.label}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Chat messages */}
                    {messages.length > 0 && (
                        <div className="rl-ai-chat-log">
                            {messages.map((msg, i) => {
                                const actionLabel = msg.action ? AI_ACTIONS.find(a => a.key === msg.action)?.label : null;
                                return (
                                    <div key={i} className={`rl-ai-msg rl-ai-msg--${msg.role}`}>
                                        {msg.role === 'ai' && <FaRobot className="rl-ai-msg-avatar" />}
                                        <div className="rl-ai-msg-bubble">
                                            {msg.role === 'ai' && msg.action && (
                                                <div className="rl-ai-action-label">📄 {actionLabel}</div>
                                            )}
                                            {msg.text.split('\n').map((line, j) => (
                                                <p key={j} dangerouslySetInnerHTML={{
                                                    __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                }} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            {loading && (
                                <div className="rl-ai-msg rl-ai-msg--ai">
                                    <FaRobot className="rl-ai-msg-avatar" />
                                    <div className="rl-ai-msg-bubble rl-ai-typing">
                                        <span /><span /><span />
                                    </div>
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                    )}

                    {/* Input */}
                    <div className="rl-ai-input-area">
                        <input
                            className="rl-ai-input"
                            type="text"
                            placeholder="Consulta sobre recursos..."
                            value={question}
                            onChange={e => setQuestion(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSend()}
                            disabled={loading}
                        />
                        <button
                            className="rl-ai-send-btn"
                            onClick={handleSend}
                            disabled={loading || !question.trim()}
                        >
                            <FaPaperPlane />
                        </button>
                    </div>

                    {/* Clear button */}
                    {messages.length > 0 && (
                        <button className="rl-ai-clear-btn" onClick={() => { setMessages([]); setLastActionKey(null); }}>
                            <FaTimes /> Limpiar conversación
                        </button>
                    )}
                </>
            )}
        </aside>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────
const ResearcherLibrary = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') || 'todos';

    const [activeTab, setActiveTab] = useState(initialTab);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [activeSideNav, setActiveSideNav] = useState('biblioteca');
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedResources, setSelectedResources] = useState(new Set());

    const loadResources = async () => {
        try {
            setLoading(true);
            console.log('Fetching resources from API...');
            const response = await resourcesAPI.getByModule('REDRESEARCH');
            console.log('API Response:', response.data);
            const mappedResources = response.data.map(mapResourceToCard);
            console.log('Mapped resources:', mappedResources);
            setResources(mappedResources);
        } catch (err) {
            console.error('Error loading resources:', err);
            setResources([]);
        } finally {
            setLoading(false);
        }
    };

    // Load resources from BD on mount
    useEffect(() => {
        loadResources();
    }, []);

    const filtered = useMemo(() => {
        let r = resources;
        if (activeTab !== 'todos') r = r.filter(x => x.type.toLowerCase() === activeTab);
        if (search.trim()) {
            const q = search.toLowerCase();
            r = r.filter(x => x.title.toLowerCase().includes(q) || x.desc.toLowerCase().includes(q));
        }
        return r;
    }, [activeTab, search, resources]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    const switchTab = (key) => { setActiveTab(key); setPage(1); };

    const storagePercent = (STORAGE_USED / STORAGE_TOTAL) * 100;

    const SIDE_NAV = [
        { key: 'inicio', label: 'Inicio', icon: <FaHome />, action: () => navigate('/researcher') },
        { key: 'biblioteca', label: 'Biblioteca', icon: <FaBookOpen />, action: () => { } },
        { key: 'descargas', label: 'Mis Descargas', icon: <FaDownload />, action: () => { } },
        { key: 'favoritos', label: 'Favoritos', icon: <FaStar />, action: () => { } },
        { key: 'config', label: 'Configuración', icon: <FaCog />, action: () => { } },
    ];

    if (loading && resources.length === 0) {
        return <div className="loading-spinner">Cargando biblioteca...</div>;
    }

    return (
        <div className="rl-page">
            <header className="rl-header">
                <div className="rl-header-inner">
                    <div className="rl-header-left">
                        {user && (
                            <button className="rl-header-back-btn" onClick={() => navigate(-1)}>
                                <FaArrowLeft /> Regresar
                            </button>
                        )}
                        <SafeLink to="/" className="rl-logo">
                            <img src="/logoecseia.png" alt="Logo" className="rl-logo-img" />
                            <span className="rl-logo-eco">Eco</span>
                            <span className="rl-logo-rest">ScienceInnova</span>
                        </SafeLink>
                    </div>
                    <div className="rl-header-right">
                        <div className="rl-search-wrap">
                            <FaSearch className="rl-search-icon" />
                            <input
                                className="rl-search-input"
                                placeholder="Buscar recursos..."
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>
                        {!user && (
                            <SafeLink to="/login" className="rl-login-btn">Iniciar Sesión</SafeLink>
                        )}
                    </div>
                </div>
            </header>

            <div className="rl-body">
                {/* ── LEFT SIDEBAR ── */}
                <aside className="rl-sidebar">
                    <p className="rl-side-section-label">MENÚ PRINCIPAL</p>
                    <nav className="rl-side-nav">
                        {SIDE_NAV.map(item => (
                            <button
                                key={item.key}
                                className={`rl-side-nav-item ${activeSideNav === item.key ? 'active' : ''}`}
                                onClick={() => { setActiveSideNav(item.key); item.action(); }}
                            >
                                {item.icon} {item.label}
                            </button>
                        ))}
                    </nav>

                    <div className="rl-storage-card">
                        <p className="rl-storage-label">Espacio de Almacenamiento</p>
                        <div className="rl-storage-bar-bg">
                            <div className="rl-storage-bar-fill" style={{ width: `${storagePercent}%` }} />
                        </div>
                        <p className="rl-storage-text">{STORAGE_USED} GB de {STORAGE_TOTAL} GB utilizados</p>
                    </div>

                    <div className="rl-sidebar-footer">
                        <img src="/logoecseia.png" alt="Logo" className="rl-sf-logo" />
                        <span className="rl-sf-text">EcoScienceInnova © 2024</span>
                    </div>
                </aside>

                {/* ── MAIN CONTENT ── */}
                <main className="rl-main">
                    {/* Page title */}
                    <div className="rl-page-title-wrap">
                        <h1 className="rl-page-title">Recursos y Biblioteca</h1>
                        <p className="rl-page-subtitle">
                            Accede a herramientas, formatos normalizados y material de investigación científica de vanguardia para potenciar tus proyectos.
                        </p>
                    </div>

                    {/* Filter tabs */}
                    <div className="rl-tabs">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                className={`rl-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                                onClick={() => switchTab(tab.key)}
                            >
                                {tab.icon} {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Content with AI panel grid */}
                    <div className="rl-grid-with-panel">
                        {/* Cards section */}
                        <div className="rl-cards-section">
                            <div className="rl-cards-grid">
                                {paginated.length === 0 ? (
                                    <div className="rl-empty">No se encontraron recursos.</div>
                                ) : (
                                    paginated.slice(0, 3).map(r => (
                                        <ResourceCard
                                            key={r.id}
                                            resource={r}
                                            onVideoClick={setSelectedVideo}
                                            onFileClick={setSelectedFile}
                                            isSelected={selectedResources.has(r.id)}
                                            onToggleSelect={(id) => setSelectedResources(prev => {
                                                const next = new Set(prev);
                                                next.has(id) ? next.delete(id) : next.add(id);
                                                return next;
                                            })}
                                        />
                                    ))
                                )}
                            </div>

                        </div>

                        {/* AI Panel */}
                        <ResearcherAiPanel
                            selectedResources={selectedResources}
                            onClearSelection={() => setSelectedResources(new Set())}
                            resources={resources}
                        />
                    </div>

                    <div className="rl-footer-row">
                        <span className="rl-count">
                            Mostrando {Math.min(page * ITEMS_PER_PAGE, filtered.length)} de {filtered.length} recursos
                        </span>
                        {totalPages > 1 && (
                            <div className="rl-pagination">
                                <button className="rl-pag-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                    <FaChevronLeft />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                                    <button
                                        key={n}
                                        className={`rl-pag-btn ${n === page ? 'active' : ''}`}
                                        onClick={() => setPage(n)}
                                    >
                                        {n}
                                    </button>
                                ))}
                                <button className="rl-pag-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                    <FaChevronRight />
                                </button>
                            </div>
                        )}
                    </div>
                </main>
            </div>

            {/* ── BOTTOM FOOTER ── */}
            <footer className="rl-footer">
                <div className="rl-footer-inner">
                    <span className="rl-footer-brand">
                        <img src="/logoecseia.png" alt="Logo" className="rl-footer-logo" />
                        <span><span style={{ color: '#FF6600' }}>Eco</span>ScienceInnova © 2024</span>
                    </span>
                    <div className="rl-footer-links">
                        <a href="#privacidad">Privacidad</a>
                        <a href="#terminos">Términos de Uso</a>
                        <a href="#soporte">Soporte Técnico</a>
                    </div>
                </div>
            </footer>

            {/* Video Modal */}
            <VideoModal resource={selectedVideo} isOpen={!!selectedVideo} onClose={() => setSelectedVideo(null)} />

            {/* File Preview Modal */}
            <FilePreviewModal resource={selectedFile} isOpen={!!selectedFile} onClose={() => setSelectedFile(null)} />
        </div>
    );
};

export default ResearcherLibrary;
